import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../api";
import { clearAppToken } from "../auth";
import {
  P0_BY_PLANET_TYPE,
  PLANET_TYPES,
  type PlanetType,
} from "../constants";
import { ProductIcon, useTypeIds } from "../icons";
import { useLocale, useT } from "../i18n";
import type { Locale } from "../i18n";
import { LocaleSwitcher } from "../LocaleSwitcher";
import { localizeProductName, localizePlanetType, localizeHaulerName, localizeSellStrategy, localizeSolverStatus, localizeJobStatus } from "../translations";
import type {
  CreditBalanceResponse,
  JobStatusResponse,
  OptimizeRequest,
  OptimizeResponse,
  Principal,
  SellStrategy,
} from "../types";

interface PlanetEditorRow {
  id: string;
  name: string;
  type: PlanetType;
  // Radius in km. Used together with linkLengthFactorPerFacility to derive
  // average link length per facility (and therefore per-link Power/CPU cost).
  radiusKm: number;
  // Surveyed P0 yield per extractor head per hour. Keys are restricted at
  // render time to P0_BY_PLANET_TYPE[type]; values default to 0 (pure
  // manufacturing).
  yields: Record<string, number>;
}

const HAULER_SHIPS = ["Epithal", "Squall", "Deluge", "Torrent"] as const;

// Bandwidth bonus skill keyed by ship. Mirrors `pi_hauler_ships` in the
// backend; the LP scales each ship's hold by (1 + 0.10 * skill_level).
const HAULER_SKILL_BY_SHIP: Record<(typeof HAULER_SHIPS)[number], string> = {
  Epithal: "Gallente Industrial",
  Squall: "Upwell Hauler",
  Deluge: "Upwell Hauler",
  Torrent: "Upwell Hauler",
};
const SELL_STRATEGIES: SellStrategy[] = [
  "to_buy_order",
  "at_median",
  "at_sell_order",
];

const POLL_INTERVAL_MS = 2000;

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function emptyYieldsForType(type: PlanetType): Record<string, number> {
  return Object.fromEntries(P0_BY_PLANET_TYPE[type].map((p) => [p, 0]));
}

function defaultPlanets(): PlanetEditorRow[] {
  // Friendly default: a single 10,000 km Barren planet with no surveyed yields.
  // Users can add more or change the type before running an optimization.
  return [
    {
      id: makeId(),
      name: "Barren 1",
      type: "Barren" as PlanetType,
      radiusKm: 10000,
      yields: emptyYieldsForType("Barren"),
    },
  ];
}

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function describeError(err: unknown, t?: ReturnType<typeof useT>): string {
  if (err instanceof ApiError) {
    if (
      t &&
      err.status === 402 &&
      typeof err.detail === "object" &&
      err.detail !== null &&
      "reason" in (err.detail as Record<string, unknown>) &&
      (err.detail as { reason: string }).reason === "insufficient_credits"
    ) {
      const detail = err.detail as {
        balance: number;
        required: number;
      };
      return t.insufficientCredits(detail.required, detail.balance);
    }
    if (typeof err.detail === "string") return err.detail;
    return JSON.stringify(err.detail);
  }
  return (err as Error).message;
}

export function OptimizePage() {
  const navigate = useNavigate();
  const t = useT();
  const { locale } = useLocale();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const typeIds = useTypeIds();

  const [planets, setPlanets] = useState<PlanetEditorRow[]>(defaultPlanets);
  const [planetsExpanded, setPlanetsExpanded] = useState(true);

  const [tripsPerWeek, setTripsPerWeek] = useState(7);
  const [pocoTaxRate, setPocoTaxRate] = useState(0.1);
  const [interplanetCourierCost, setInterplanetCourierCost] = useState(10);
  const [sellStrategy, setSellStrategy] = useState<SellStrategy>("to_buy_order");
  const [commandCenterLevel, setCommandCenterLevel] = useState(4);
  const [accountingLevel, setAccountingLevel] = useState(5);
  const [haulerShip, setHaulerShip] = useState<string>("Epithal");
  const [haulerSkillLevel, setHaulerSkillLevel] = useState(5);
  const [linkLengthFactorPerFacility, setLinkLengthFactorPerFacility] =
    useState(0.013);

  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [result, setResult] = useState<OptimizeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [credits, setCredits] = useState<CreditBalanceResponse | null>(null);

  const refreshCredits = useCallback(() => {
    api.getCredits().then(setCredits).catch(() => {
      // Non-fatal: keep last known balance if the lookup fails.
    });
  }, []);

  useEffect(() => {
    api
      .me()
      .then((r) => setPrincipal(r.principal))
      .catch(() => {
        clearAppToken();
        navigate("/login", { replace: true });
      });
    refreshCredits();
  }, [navigate, refreshCredits]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const status = await api.getJobStatus(jobId);
        if (cancelled) return;
        setJobStatus(status);
        if (status.status === "succeeded") {
          const final = await api.getJobResult(jobId);
          if (cancelled) return;
          setResult(final.result);
          setSubmitting(false);
          refreshCredits();
          return;
        }
        if (status.status === "failed") {
          setError(status.error ?? "Optimization failed");
          setSubmitting(false);
          refreshCredits();
          return;
        }
        timer = window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(describeError(err, t));
        setSubmitting(false);
      }
    };
    tick();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [jobId]);

  const planetNames = useMemo(() => planets.map((p) => p.name), [planets]);

  const planetTypeCounts = useMemo(() => {
    const counts = new Map<PlanetType, number>();
    for (const planet of planets) {
      counts.set(planet.type, (counts.get(planet.type) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [planets]);

  const planetSummary = planetTypeCounts.length
    ? planetTypeCounts.map(([type, n]) => `${n} ${localizePlanetType(type, locale)}`).join(", ")
    : "none";

  function updatePlanet(id: string, patch: Partial<PlanetEditorRow>): void {
    setPlanets((prev) =>
      prev.map((planet) => (planet.id === id ? { ...planet, ...patch } : planet)),
    );
  }

  function changePlanetType(id: string, type: PlanetType): void {
    setPlanets((prev) =>
      prev.map((planet) => {
        if (planet.id !== id) return planet;
        // Reset yields to the new type's resource set, preserving overlapping values.
        const next = emptyYieldsForType(type);
        for (const key of Object.keys(next)) {
          if (key in planet.yields) {
            next[key] = planet.yields[key];
          }
        }
        return { ...planet, type, yields: next };
      }),
    );
  }

  function setYield(id: string, p0: string, value: number): void {
    setPlanets((prev) =>
      prev.map((planet) => {
        if (planet.id !== id) return planet;
        return { ...planet, yields: { ...planet.yields, [p0]: value } };
      }),
    );
  }

  function addPlanet(type: PlanetType = "Barren"): void {
    const baseName = `${type} ${planets.length + 1}`;
    setPlanets((prev) => [
      ...prev,
      {
        id: makeId(),
        name: baseName,
        type,
        radiusKm: 5000,
        yields: emptyYieldsForType(type),
      },
    ]);
  }

  function removePlanet(id: string): void {
    setPlanets((prev) => prev.filter((planet) => planet.id !== id));
  }

  function validate(): string | null {
    if (planets.length === 0) {
      return "Add at least one planet.";
    }
    const seen = new Set<string>();
    for (const planet of planets) {
      const trimmed = planet.name.trim();
      if (!trimmed) return "Every planet needs a name.";
      if (seen.has(trimmed)) return `Duplicate planet name: ${trimmed}`;
      seen.add(trimmed);
      if (Number.isNaN(planet.radiusKm) || planet.radiusKm <= 0) {
        return `${trimmed}: radius must be a positive number of km.`;
      }
      for (const [p0, value] of Object.entries(planet.yields)) {
        if (Number.isNaN(value) || value < 0) {
          return `${trimmed}: ${p0} yield must be a non-negative number.`;
        }
      }
    }
    return null;
  }

  function buildRequest(): OptimizeRequest {
    const planetsPayload: Record<string, Record<string, number>> = {};
    const planetTypesPayload: Record<string, string> = {};
    const planetRadiiPayload: Record<string, number> = {};
    for (const planet of planets) {
      const name = planet.name.trim();
      // Drop zero-yield entries: backend treats missing P0 as a non-extracted resource.
      const nonZero: Record<string, number> = {};
      for (const [p0, value] of Object.entries(planet.yields)) {
        if (value > 0) nonZero[p0] = value;
      }
      planetsPayload[name] = nonZero;
      planetTypesPayload[name] = planet.type;
      planetRadiiPayload[name] = planet.radiusKm;
    }
    return {
      planets: planetsPayload,
      planet_types: planetTypesPayload,
      planet_radii_km: planetRadiiPayload,
      link_length_factor_per_facility: linkLengthFactorPerFacility,
      trips_per_week: tripsPerWeek,
      poco_tax_rate: pocoTaxRate,
      interplanet_courier_cost_per_m3: interplanetCourierCost,
      sell_strategy: sellStrategy,
      command_center_level: commandCenterLevel,
      accounting_level: accountingLevel,
      hauler_ship: haulerShip,
      hauler_skill_levels: {
        [HAULER_SKILL_BY_SHIP[haulerShip as keyof typeof HAULER_SKILL_BY_SHIP]]:
          haulerSkillLevel,
      },
    };
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const validationMessage = validate();
    if (validationMessage) {
      setValidationError(validationMessage);
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    setError(null);
    setResult(null);
    setJobStatus(null);
    setJobId(null);

    try {
      const accepted = await api.createJob(buildRequest());
      setJobId(accepted.job_id);
      setJobStatus({
        job_id: accepted.job_id,
        status: accepted.status,
        created_at: new Date().toISOString(),
        started_at: null,
        finished_at: null,
        error: null,
        reserved_credits: accepted.reserved_credits,
        actual_credits: 0,
      });
      refreshCredits();
    } catch (err) {
      setError(describeError(err, t));
      setSubmitting(false);
      refreshCredits();
    }
  }

  function handleLogout() {
    clearAppToken();
    navigate("/login", { replace: true });
  }

  return (
    <div className="page">
      <header className="app-header">
        <h1>{t.appTitle}</h1>
        <div className="header-tools">
          {credits && <CreditPill credits={credits} />}
          <LocaleSwitcher />
          {principal && (
            <div>
              <span className="muted">{t.loggedInAs} </span>
              <strong>{principal.character_name}</strong>{" "}
              <button className="secondary" onClick={handleLogout}>
                {t.logout}
              </button>
            </div>
          )}
        </div>
      </header>

      <form onSubmit={handleSubmit}>
        <section className="card">
          <div className="card-header">
            <button
              type="button"
              className="disclosure"
              onClick={() => setPlanetsExpanded((open) => !open)}
              aria-expanded={planetsExpanded}
              aria-controls="planet-list"
            >
              <span className="chevron">{planetsExpanded ? "▾" : "▸"}</span>
              <h2>{t.planetsHeading}</h2>
              <span className="summary-pill">
                {t.planetsTotalSummary(planets.length)} · {planetSummary}
              </span>
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setPlanetsExpanded(true);
                addPlanet("Barren");
              }}
            >
              {t.addPlanet}
            </button>
          </div>
          {planetsExpanded && (
            <>
              <p className="muted">{t.planetsHelp}</p>

              <div id="planet-list" className="planet-list">
                {planets.map((planet) => (
                  <PlanetEditor
                    key={planet.id}
                    planet={planet}
                    otherNames={planetNames.filter((n) => n !== planet.name)}
                    typeIds={typeIds}
                    locale={locale}
                    onRename={(name) => updatePlanet(planet.id, { name })}
                    onTypeChange={(type) => changePlanetType(planet.id, type)}
                    onRadiusChange={(radiusKm) =>
                      updatePlanet(planet.id, { radiusKm })
                    }
                    onYieldChange={(p0, value) => setYield(planet.id, p0, value)}
                    onRemove={() => removePlanet(planet.id)}
                    disableRemove={planets.length <= 1}
                  />
                ))}
              </div>
            </>
          )}
        </section>

        <section className="card">
          <h2>{t.runParameters}</h2>
          <div className="optimize-form">
            <label>
              {t.tripsPerWeek}
              <input
                type="number"
                min={0}
                step={0.5}
                value={tripsPerWeek}
                onChange={(e) => setTripsPerWeek(Number(e.target.value))}
              />
            </label>
            <label>
              {t.pocoTaxRate}
              <input
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={pocoTaxRate}
                onChange={(e) => setPocoTaxRate(Number(e.target.value))}
              />
            </label>
            <label>
              {t.interplanetIskPerM3}
              <input
                type="number"
                min={0}
                step={1}
                value={interplanetCourierCost}
                onChange={(e) => setInterplanetCourierCost(Number(e.target.value))}
              />
            </label>
            <label>
              {t.sellStrategy}
              <select
                value={sellStrategy}
                onChange={(e) => setSellStrategy(e.target.value as SellStrategy)}
              >
                {SELL_STRATEGIES.map((value) => (
                  <option key={value} value={value}>
                    {localizeSellStrategy(value, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.commandCenterLevel}
              <input
                type="number"
                min={0}
                max={5}
                value={commandCenterLevel}
                onChange={(e) => setCommandCenterLevel(Number(e.target.value))}
              />
            </label>
            <label>
              {t.accountingLevel}
              <input
                type="number"
                min={0}
                max={5}
                value={accountingLevel}
                onChange={(e) => setAccountingLevel(Number(e.target.value))}
              />
            </label>
            <label>
              {t.haulerShip}
              <select value={haulerShip} onChange={(e) => setHaulerShip(e.target.value)}>
                {HAULER_SHIPS.map((ship) => (
                  <option key={ship} value={ship}>
                    {localizeHaulerName(ship, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.haulerSkillLevel}
              <input
                type="number"
                min={0}
                max={5}
                step={1}
                value={haulerSkillLevel}
                onChange={(e) => setHaulerSkillLevel(Number(e.target.value))}
              />
            </label>
            <label>
              {t.linkLengthFactor}
              <input
                type="number"
                min={0}
                step={0.001}
                value={linkLengthFactorPerFacility}
                onChange={(e) =>
                  setLinkLengthFactorPerFacility(Number(e.target.value))
                }
              />
            </label>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <button type="submit" disabled={submitting}>
              {submitting ? t.optimizing : t.runOptimization}
            </button>
          </div>
        </section>
      </form>

      {validationError && <p className="error">{validationError}</p>}

      {jobStatus && !result && !error && (
        <p className="muted">
          {t.jobLabel} <code>{jobStatus.job_id}</code> — {t.status.toLowerCase()}{" "}
          <strong>{localizeJobStatus(jobStatus.status, locale)}</strong>
        </p>
      )}

      {error && <p className="error">{error}</p>}

      {result && <ResultView result={result} typeIds={typeIds} locale={locale} />}
    </div>
  );
}

interface PlanetEditorProps {
  planet: PlanetEditorRow;
  otherNames: string[];
  typeIds: Record<string, number> | null;
  locale: Locale;
  onRename: (name: string) => void;
  onTypeChange: (type: PlanetType) => void;
  onRadiusChange: (radiusKm: number) => void;
  onYieldChange: (p0: string, value: number) => void;
  onRemove: () => void;
  disableRemove: boolean;
}

function PlanetEditor({
  planet,
  otherNames,
  typeIds,
  locale,
  onRename,
  onTypeChange,
  onRadiusChange,
  onYieldChange,
  onRemove,
  disableRemove,
}: PlanetEditorProps) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const duplicate =
    planet.name.trim().length > 0 && otherNames.includes(planet.name.trim());
  const surveyedCount = Object.values(planet.yields).filter((v) => v > 0).length;

  return (
    <div className="planet-row">
      <div className="planet-row-head">
        <button
          type="button"
          className="chevron-button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse planet" : "Expand planet"}
        >
          {expanded ? "▾" : "▸"}
        </button>
        <input
          type="text"
          value={planet.name}
          onChange={(e) => onRename(e.target.value)}
          aria-label={t.planetName}
          className={duplicate ? "input-error" : undefined}
        />
        <select
          value={planet.type}
          onChange={(e) => onTypeChange(e.target.value as PlanetType)}
          aria-label={t.planetType}
        >
          {PLANET_TYPES.map((type) => (
            <option key={type} value={type}>
              {localizePlanetType(type, locale)}
            </option>
          ))}
        </select>
        <label className="radius-input" aria-label={`${t.planetType} ${t.radius}`}>
          <span>{t.radius}</span>
          <input
            type="number"
            min={1}
            step="any"
            value={planet.radiusKm}
            onChange={(e) => onRadiusChange(Number(e.target.value))}
          />
          <span className="muted">{t.km}</span>
        </label>
        <span className="muted yields-count">
          {surveyedCount === 0 ? t.noSurveyedYields : t.surveyedCount(surveyedCount)}
        </span>
        <button
          type="button"
          className="secondary"
          onClick={onRemove}
          disabled={disableRemove}
          aria-label={t.remove}
        >
          {t.remove}
        </button>
      </div>
      {expanded && (
        <div className="planet-yields">
          {P0_BY_PLANET_TYPE[planet.type].map((p0) => (
            <label key={p0} className="yield-input">
              <span className="yield-label">
                <ProductIcon name={p0} size={20} typeIds={typeIds} />
                {localizeProductName(p0, locale)}
              </span>
              <input
                type="number"
                min={0}
                step={1}
                value={planet.yields[p0] ?? 0}
                onChange={(e) => onYieldChange(p0, Number(e.target.value))}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultView({
  result,
  typeIds,
  locale,
}: {
  result: OptimizeResponse;
  typeIds: Record<string, number> | null;
  locale: Locale;
}) {
  const t = useT();
  const factoryRows: Array<{ planet: string; tier: string; product: string; count: number }> = [];
  for (const [planet, tiers] of Object.entries(result.factories)) {
    for (const [tier, products] of Object.entries(tiers)) {
      for (const [product, count] of Object.entries(products)) {
        if (count > 0) {
          factoryRows.push({ planet, tier, product, count });
        }
      }
    }
  }

  return (
    <>
      <section className="card">
        <h2>{t.summary}</h2>
        <ul className="summary">
          <li>
            <span>{t.status}</span>
            <span>{localizeSolverStatus(result.status, locale)}</span>
          </li>
          <li>
            <span>{t.netProfitPerHr}</span>
            <span>{formatNumber(result.total_value_per_hour)} ISK</span>
          </li>
          <li>
            <span>{t.grossExportPerHr}</span>
            <span>{formatNumber(result.net_export_value_per_hour)} ISK</span>
          </li>
          <li>
            <span>{t.totalImportCostPerHr}</span>
            <span>{formatNumber(result.net_import_cost_per_hour)} ISK</span>
          </li>
          <li>
            <span>{t.margin}</span>
            <span>
              {result.margin_percentage === null
                ? "—"
                : `${result.margin_percentage.toFixed(2)}%`}
            </span>
          </li>
          <li>
            <span>{t.netExportVolumePerHr}</span>
            <span>{formatNumber(result.net_export_size_m3_per_hour, 1)} m³</span>
          </li>
          <li>
            <span>{t.netImportVolumePerHr}</span>
            <span>{formatNumber(result.net_import_size_m3_per_hour, 1)} m³</span>
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>{t.netProduction}</h2>
        {Object.entries(result.net_production).map(([tier, products]) => (
          <div key={tier}>
            <h3>{tier}</h3>
            <table className="production">
              <thead>
                <tr>
                  <th>{t.product}</th>
                  <th>{t.perHour}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(products).map(([product, amount]) => (
                  <tr key={product}>
                    <td>
                      <span className="name-with-icon">
                        <ProductIcon name={product} size={20} typeIds={typeIds} />
                        {localizeProductName(product, locale)}
                      </span>
                    </td>
                    <td>{formatNumber(amount, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>

      <section className="card">
        <h2>{t.factoriesPerPlanet}</h2>
        {factoryRows.length === 0 ? (
          <p className="muted">{t.noFactoriesAllocated}</p>
        ) : (
          <table className="production">
            <thead>
              <tr>
                <th>{t.planet}</th>
                <th>{t.tier}</th>
                <th>{t.product}</th>
                <th>{t.count}</th>
              </tr>
            </thead>
            <tbody>
              {factoryRows.map(({ planet, tier, product, count }) => (
                <tr key={`${planet}-${tier}-${product}`}>
                  <td>{planet}</td>
                  <td>{tier}</td>
                  <td>
                    <span className="name-with-icon">
                      <ProductIcon name={product} size={20} typeIds={typeIds} />
                      {localizeProductName(product, locale)}
                    </span>
                  </td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <PerPlanetView result={result} typeIds={typeIds} locale={locale} />
    </>
  );
}

const TIER_ORDER = ["P0", "P1", "P2", "P3", "P4"] as const;

interface FlowEntry {
  product: string;
  amount: number;
}

function nonZeroEntries(map: Record<string, number> | undefined): FlowEntry[] {
  if (!map) return [];
  return Object.entries(map)
    .filter(([, v]) => Math.abs(v) > 1e-6)
    .map(([product, amount]) => ({ product, amount }))
    .sort((a, b) => b.amount - a.amount);
}

interface TransferEntry {
  product: string;
  partner: string;
  amount: number;
}

function transfersForPlanet(
  transfers: Record<string, Record<string, Record<string, Record<string, number>>>>,
  planet: string,
  direction: "in" | "out",
): Record<string, TransferEntry[]> {
  const result: Record<string, TransferEntry[]> = {};
  for (const tier of Object.keys(transfers)) {
    const entries: TransferEntry[] = [];
    const tierFlows = transfers[tier];
    if (direction === "out") {
      const fromPlanet = tierFlows[planet] ?? {};
      for (const [dst, products] of Object.entries(fromPlanet)) {
        for (const [product, amount] of Object.entries(products)) {
          if (Math.abs(amount) > 1e-6) {
            entries.push({ product, partner: dst, amount });
          }
        }
      }
    } else {
      for (const [src, dstMap] of Object.entries(tierFlows)) {
        const products = dstMap[planet];
        if (!products) continue;
        for (const [product, amount] of Object.entries(products)) {
          if (Math.abs(amount) > 1e-6) {
            entries.push({ product, partner: src, amount });
          }
        }
      }
    }
    if (entries.length > 0) {
      entries.sort((a, b) => b.amount - a.amount);
      result[tier] = entries;
    }
  }
  return result;
}

function FlowTable({
  entries,
  label,
  typeIds,
  locale,
}: {
  entries: FlowEntry[];
  label: string;
  typeIds: Record<string, number> | null;
  locale: Locale;
}) {
  const t = useT();
  if (entries.length === 0) return null;
  return (
    <table className="production">
      <thead>
        <tr>
          <th>{t.product}</th>
          <th>{label}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(({ product, amount }) => (
          <tr key={product}>
            <td>
              <span className="name-with-icon">
                <ProductIcon name={product} size={20} typeIds={typeIds} />
                {localizeProductName(product, locale)}
              </span>
            </td>
            <td>{formatNumber(amount, 2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TransferTable({
  byTier,
  partnerHeader,
  typeIds,
  locale,
}: {
  byTier: Record<string, TransferEntry[]>;
  partnerHeader: string;
  typeIds: Record<string, number> | null;
  locale: Locale;
}) {
  const t = useT();
  const tiers = TIER_ORDER.filter((tier) => byTier[tier]?.length);
  if (tiers.length === 0) return null;
  return (
    <table className="production">
      <thead>
        <tr>
          <th>{t.tier}</th>
          <th>{t.product}</th>
          <th>{partnerHeader}</th>
          <th>{t.perHour}</th>
        </tr>
      </thead>
      <tbody>
        {tiers.flatMap((tier) =>
          byTier[tier].map((entry) => (
            <tr key={`${tier}-${entry.product}-${entry.partner}`}>
              <td>{tier}</td>
              <td>
                <span className="name-with-icon">
                  <ProductIcon name={entry.product} size={20} typeIds={typeIds} />
                  {localizeProductName(entry.product, locale)}
                </span>
              </td>
              <td>{entry.partner}</td>
              <td>{formatNumber(entry.amount, 2)}</td>
            </tr>
          )),
        )}
      </tbody>
    </table>
  );
}

function PerPlanetView({
  result,
  typeIds,
  locale,
}: {
  result: OptimizeResponse;
  typeIds: Record<string, number> | null;
  locale: Locale;
}) {
  const t = useT();
  const planetNames = useMemo(() => {
    const names = new Set<string>();
    for (const map of [
      result.jita_imports,
      result.jita_exports,
      result.local_consumption,
      result.factories,
    ]) {
      if (!map) continue;
      for (const name of Object.keys(map)) names.add(name);
    }
    return Array.from(names).sort();
  }, [result]);

  if (planetNames.length === 0) return null;

  return (
    <section className="card">
      <h2>{t.perPlanetFlows}</h2>
      <p className="muted">{t.perPlanetFlowsHelp}</p>
      <div className="planet-flows">
        {planetNames.map((planet) => (
          <PlanetFlowCard
            key={planet}
            planet={planet}
            result={result}
            typeIds={typeIds}
            locale={locale}
          />
        ))}
      </div>
    </section>
  );
}

function PlanetFlowCard({
  planet,
  result,
  typeIds,
  locale,
}: {
  planet: string;
  result: OptimizeResponse;
  typeIds: Record<string, number> | null;
  locale: Locale;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(true);
  const importsByTier = result.jita_imports?.[planet] ?? {};
  const exportsByTier = result.jita_exports?.[planet] ?? {};
  const transfersIn = useMemo(
    () => transfersForPlanet(result.inter_planet_transfers ?? {}, planet, "in"),
    [planet, result.inter_planet_transfers],
  );
  const transfersOut = useMemo(
    () => transfersForPlanet(result.inter_planet_transfers ?? {}, planet, "out"),
    [planet, result.inter_planet_transfers],
  );

  const tiersWithImports = TIER_ORDER.filter(
    (tier) => nonZeroEntries(importsByTier[tier]).length > 0,
  );
  const tiersWithExports = TIER_ORDER.filter(
    (tier) => nonZeroEntries(exportsByTier[tier]).length > 0,
  );

  const importCount = tiersWithImports.reduce(
    (acc, tier) => acc + nonZeroEntries(importsByTier[tier]).length,
    0,
  );
  const exportCount = tiersWithExports.reduce(
    (acc, tier) => acc + nonZeroEntries(exportsByTier[tier]).length,
    0,
  );
  const transfersInCount = Object.values(transfersIn).reduce(
    (acc, list) => acc + list.length,
    0,
  );
  const transfersOutCount = Object.values(transfersOut).reduce(
    (acc, list) => acc + list.length,
    0,
  );

  return (
    <div className="planet-flow-card">
      <button
        type="button"
        className="disclosure"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <span className="chevron">{expanded ? "▾" : "▸"}</span>
        <h3>{planet}</h3>
        <span className="summary-pill">
          {t.flowSummaryPill(importCount, exportCount, transfersInCount, transfersOutCount)}
        </span>
      </button>
      {expanded && (
        <div className="planet-flow-body">
          <FlowSubsection title={t.jitaImports}>
            {tiersWithImports.length === 0 ? (
              <p className="muted">{t.none}</p>
            ) : (
              tiersWithImports.map((tier) => (
                <div key={tier}>
                  <h4>{tier}</h4>
                  <FlowTable
                    entries={nonZeroEntries(importsByTier[tier])}
                    label={t.perHour}
                    typeIds={typeIds}
                    locale={locale}
                  />
                </div>
              ))
            )}
          </FlowSubsection>

          <FlowSubsection title={t.jitaExportsNet}>
            {tiersWithExports.length === 0 ? (
              <p className="muted">{t.none}</p>
            ) : (
              tiersWithExports.map((tier) => (
                <div key={tier}>
                  <h4>{tier}</h4>
                  <FlowTable
                    entries={nonZeroEntries(exportsByTier[tier])}
                    label={t.perHour}
                    typeIds={typeIds}
                    locale={locale}
                  />
                </div>
              ))
            )}
          </FlowSubsection>

          <FlowSubsection title={t.transfersIn}>
            {Object.keys(transfersIn).length === 0 ? (
              <p className="muted">{t.none}</p>
            ) : (
              <TransferTable
                byTier={transfersIn}
                partnerHeader={t.fromPlanet}
                typeIds={typeIds}
                locale={locale}
              />
            )}
          </FlowSubsection>

          <FlowSubsection title={t.transfersOut}>
            {Object.keys(transfersOut).length === 0 ? (
              <p className="muted">{t.none}</p>
            ) : (
              <TransferTable
                byTier={transfersOut}
                partnerHeader={t.toPlanet}
                typeIds={typeIds}
                locale={locale}
              />
            )}
          </FlowSubsection>
        </div>
      )}
    </div>
  );
}

function FlowSubsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="planet-flow-subsection">
      <h4 className="subsection-title">{title}</h4>
      {children}
    </div>
  );
}

function CreditPill({ credits }: { credits: CreditBalanceResponse }) {
  const t = useT();
  const insufficient = credits.balance < credits.submit_estimate;
  const deficit = Math.max(0, credits.submit_estimate - credits.balance);
  // ETA assumes the user is the only refill recipient. With many active
  // users it could take longer; the help text covers the actual rate.
  const etaSeconds =
    credits.refill_rate_per_second > 0 ? deficit / credits.refill_rate_per_second : Infinity;
  const status = credits.balance <= 0 ? t.creditsExhausted : t.creditBalance;
  return (
    <div
      className={`credit-pill${insufficient ? " credit-pill-low" : ""}`}
      title={t.creditsRefilling(credits.refill_rate_per_second)}
    >
      <span className="muted">{status}:</span>{" "}
      <strong>
        {credits.balance.toFixed(0)} / {credits.max_balance.toFixed(0)}
      </strong>
      {insufficient && Number.isFinite(etaSeconds) && (
        <span className="muted credit-eta"> · {t.creditsRefillEta(etaSeconds)}</span>
      )}
    </div>
  );
}
